import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router";
import Layout from "../../components/Layout/Layout";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import * as XLSX from 'xlsx';
import styles from "./PaymentPage.module.scss";
import { API_BASE_URL, getWithFallback } from "../../config/api";
import { formatPhoneInput, validatePhone } from "../../utils/phone";

interface ReceiptInfo {
  id: number;
  has_pdf: boolean;
  updated_at?: string;
  payment_id?: string;
  receipt_url?: string;
  receipt_type?: string;
}

interface PaymentHistoryItem {
  id: number;
  total_amount: number;
  description?: string;
  email?: string;
  status: string;
  created_at: string;
  receipt?: ReceiptInfo;
  buyer_name?: string;
  supplier_name?: string;
  receipt_url?: string;
  receipt_type?: string;
}

interface PaymentHistoryResponse {
  items: PaymentHistoryItem[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

interface ExcelRow {
  '№': number;
  'Товар': string;
  'Серия': string;
  'Шир': number;
  'Состав': string;
  'Пр-во': string;
  'Количество': number;
  'Цена, руб': number;
  'Сумма, руб': number;
}

interface ExcelData {
  supplier: string;
  buyer: string;
  totalAmount: number;
  items: ExcelRow[];
  documentNumber?: string;
  documentDate?: string;
}

interface Customer {
  id: number;
  full_name: string;
  phone?: string;
  email?: string;
  created_at: string;
  updated_at: string;
}


const normalizeHeaderCell = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const findProductColumnIndex = (textRow: string[]): number =>
  textRow.findIndex((v) => {
    const s = normalizeHeaderCell(v);
    return (
      s.includes('товар') ||
      s.includes('наименование') ||
      s === 'название' ||
      s.includes('работы, услуги')
    );
  });


const findSumColumnIndex = (textRow: string[]): number => {
  let lastMatch = -1;
  for (let i = 0; i < textRow.length; i++) {
    const s = normalizeHeaderCell(textRow[i]);
    if (s === 'сумма' || (s.startsWith('сумм') && !s.includes('ндс') && !s.includes('ставка'))) {
      lastMatch = i;
    }
  }
  return lastMatch;
};

const rowLooksLikeTotal = (row: unknown[]): boolean =>
  row.some((cell) => {
    if (typeof cell !== 'string') return false;
    const lower = cell.toLowerCase();
    return (
      lower.includes('итого') ||
      lower.includes('всего наименований') ||
      lower.startsWith('в т.ч.')
    );
  });

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [email] = useState(() => localStorage.getItem("userEmail") || "");
  const [role] = useState(() => localStorage.getItem("userRole") || "");
  const [excelData, setExcelData] = useState<ExcelData | null>(null);
  const [editableItems, setEditableItems] = useState<ExcelRow[]>([]);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [history, setHistory] = useState<PaymentHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState<number>(1);
  const [historyTotalPages, setHistoryTotalPages] = useState<number>(1);
  const [historyTotal, setHistoryTotal] = useState<number>(0);
  const [isHistoryLoading, setIsHistoryLoading] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<PaymentHistoryItem | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState<string>('');
  const [historySortDirection, setHistorySortDirection] = useState<'asc' | 'desc'>('desc');
  const [historyLoadError, setHistoryLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fetchingReceiptLinks, setFetchingReceiptLinks] = useState<{[key: number]: boolean}>({});
  const HISTORY_LIMIT = 25;


  const [prepaymentFullName, setPrepaymentFullName] = useState<string>('');
  const [prepaymentEmail, setPrepaymentEmail] = useState<string>('');
  const [prepaymentPhone, setPrepaymentPhone] = useState<string>('');
  const [prepaymentAmount, setPrepaymentAmount] = useState<string>('');
  const [prepaymentConsent, setPrepaymentConsent] = useState<boolean>(false);
  const [isPrepaymentLoading, setIsPrepaymentLoading] = useState<boolean>(false);
  const [prepaymentUrl, setPrepaymentUrl] = useState<string | null>(null);


  const [customerFullName, setCustomerFullName] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [customerConsent, setCustomerConsent] = useState<boolean>(false);

  const [linkedPrepaymentNumber, setLinkedPrepaymentNumber] = useState<string>('');
  const [linkedPrepaymentAmount, setLinkedPrepaymentAmount] = useState<string>('');
  const [paymentDelivery, setPaymentDelivery] = useState<string>('');
  const [receipts, setReceipts] = useState<Record<number, any>>({});
  const [fetchingReceipts, setFetchingReceipts] = useState<Record<number, boolean>>({});
  const [refundingOrders, setRefundingOrders] = useState<Record<number, boolean>>({});


  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [selectedPrepaymentCustomerId, setSelectedPrepaymentCustomerId] = useState<number | null>(null);
  const [showCustomerForm, setShowCustomerForm] = useState<boolean>(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [newCustomerFullName, setNewCustomerFullName] = useState<string>('');
  const [newCustomerPhone, setNewCustomerPhone] = useState<string>('');
  const [newCustomerEmail, setNewCustomerEmail] = useState<string>('');
  const [isLoadingCustomers, setIsLoadingCustomers] = useState<boolean>(false);
  const [isSavingCustomer, setIsSavingCustomer] = useState<boolean>(false);
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [customersListCollapsed, setCustomersListCollapsed] = useState<boolean>(true);


  const [oneCItems, setOneCItems] = useState<any[]>([]);
  const [selectedOneCItemId, setSelectedOneCItemId] = useState<number | null>(null);
  const [isLoadingOneCItems, setIsLoadingOneCItems] = useState<boolean>(false);
  const [oneCItemSearch, setOneCItemSearch] = useState<string>('');


  interface Manager {
    id: number;
    email: string;
    full_name?: string;
    phone?: string;
    role: string;
    is_active: boolean;
    is_verified: boolean;
    created_at: string;
  }
  const [managers, setManagers] = useState<Manager[]>([]);
  const [showManagerForm, setShowManagerForm] = useState<boolean>(false);
  const [editingManager, setEditingManager] = useState<Manager | null>(null);
  const [managerEmail, setManagerEmail] = useState<string>('');
  const [managerFullName, setManagerFullName] = useState<string>('');
  const [managerPhone, setManagerPhone] = useState<string>('');
  const [managerPassword, setManagerPassword] = useState<string>('');
  const [managerIsActive, setManagerIsActive] = useState<boolean>(true);
  const [managerIsVerified, setManagerIsVerified] = useState<boolean>(true);
  const [isLoadingManagers, setIsLoadingManagers] = useState<boolean>(false);
  const [isSavingManager, setIsSavingManager] = useState<boolean>(false);
  const skipInitialCustomersSearchRef = React.useRef(false);
  const skipInitialOneCSearchRef = React.useRef(false);
  const historyLoadRequestIdRef = React.useRef(0);

  const normalizedRole = (role || '').toLowerCase();
  const isAdmin = normalizedRole === 'admin';
  const isManager = normalizedRole === 'manager';

  console.log('=== DIAGNOSTIC ===');
  console.log('Role:', role);
  console.log('Email:', email);
  console.log('API_BASE_URL:', API_BASE_URL);


  const loadCustomers = async (searchTerm?: string) => {
    setIsLoadingCustomers(true);
    try {
      const token = localStorage.getItem("token");
      const params: any = {};
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const data = await getWithFallback<Customer[]>('/api/v1/customers/', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setCustomers(data);
    } catch (err: any) {
      console.error('Error loading customers:', err);
      setError('Ошибка при загрузке списка клиентов');
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const loadManagers = async () => {
    if (!isAdmin) return;
    setIsLoadingManagers(true);
    try {
      const token = localStorage.getItem("token");
      const data = await getWithFallback<Manager[]>('/api/v1/users/managers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setManagers(data);
    } catch (err: any) {
      console.error('Error loading managers:', err);
      setError('Ошибка при загрузке списка менеджеров');
    } finally {
      setIsLoadingManagers(false);
    }
  };


  const loadOneCItems = async (searchTerm?: string) => {
    setIsLoadingOneCItems(true);
    try {
      const token = localStorage.getItem("token");
      const params: any = {};
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const data = await getWithFallback<any[]>('/api/v1/payment/1c_items/list', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setOneCItems(data);
    } catch (err: any) {
      console.error('Error loading 1C items:', err);
      setError('Ошибка при загрузке списка записей из 1С');
    } finally {
      setIsLoadingOneCItems(false);
    }
  };


  const loadOneCItemDetail = async (itemId: number) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/v1/payment/1c_items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = res.data;


      setCustomerFullName(data.full_name || '');
      setCustomerEmail(data.email || '');
      setCustomerPhone(data.phone || '');


      const items: ExcelRow[] = data.items.map((item: any, index: number) => ({
        '№': index + 1,
        'Товар': item.name || '',
        'Серия': item.series || '',
        'Шир': item.width || 0,
        'Состав': item.composition || '',
        'Пр-во': item.production || '',
        'Количество': item.quantity || 0,
        'Цена, руб': item.price || 0,
        'Сумма, руб': item.amount || 0,
      }));

      setEditableItems(items);


      setExcelData({
        supplier: 'ИП Табанакова Владлена Николаевна',
        buyer: data.full_name || 'Покупатель',
        totalAmount: data.amount || 0,
        items: items,
        documentNumber: '',
        documentDate: ''
      });


      setUploadedFileName(data.order_name || `Заказ из 1С`);

      setError(null);
    } catch (err: any) {
      console.error('Error loading 1C item detail:', err);
      setError('Ошибка при загрузке данных записи из 1С');
    }
  };


  useEffect(() => {
    loadCustomers();
    if (isAdmin) {
      loadManagers();
    }
    if (isManager || isAdmin) {
      loadOneCItems();
    }

  }, [isAdmin, isManager]);


  useEffect(() => {
    if (isManager || isAdmin) {
      if (!skipInitialOneCSearchRef.current) {
        skipInitialOneCSearchRef.current = true;
        return;
      }
      const timeoutId = setTimeout(() => {
        loadOneCItems(oneCItemSearch);
      }, 500);
      return () => clearTimeout(timeoutId);
    }

  }, [oneCItemSearch, isManager, isAdmin]);


  useEffect(() => {
    if (!skipInitialCustomersSearchRef.current) {
      skipInitialCustomersSearchRef.current = true;
      return;
    }
    const timeoutId = setTimeout(() => {
      loadCustomers(customerSearch);
    }, 500);
    return () => clearTimeout(timeoutId);

  }, [customerSearch]);


  useEffect(() => {
    if (!showHistory) return;
    const delay = historySearch.trim() ? 350 : 0;
    const timeoutId = setTimeout(() => {
      loadHistoryPage(1);
    }, delay);
    return () => clearTimeout(timeoutId);

  }, [historySearch, showHistory]);


  if (!isManager && !isAdmin) {
    console.log('Access denied, role:', role);
    return (
      <Layout>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Доступ запрещён</h2>
          <p>Только для менеджеров и администраторов</p>
          <p>Ваша роль: {role}</p>
        </div>
      </Layout>
    );
  }

  console.log('Access granted, rendering payment page');


  const formatCurrency = (value: number) => {
    try {
      return value.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB' });
    } catch {
      return `${Number(value || 0).toFixed(2)} ₽`;
    }
  };


  const extractBuyerAndSupplier = (description: string) => {
    if (!description) return { buyer: null, supplier: null };

    const lines = description.split('\n');
    let buyer = null;
    let supplier = null;


    for (const line of lines) {
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith('ФИО:') && !buyer) {
        const fioValue = trimmedLine.replace('ФИО:', '').trim();
        if (fioValue && fioValue !== 'Покупатель' && fioValue !== 'Buyer') {
          buyer = fioValue;
        }
      }

      if (trimmedLine.startsWith('Поставщик:')) {
        supplier = trimmedLine.replace('Поставщик:', '').trim();
      }
    }


    if (!buyer) {

      const fioMatch = description.match(/ФИО:\s*([А-ЯЁ][а-яё]+\s+[А-ЯЁ][а-яё]+(?:\s+[А-ЯЁ][а-яё]+)?)(?:\s|$|\n)/);
      if (fioMatch) {
        const fioValue = fioMatch[1].trim();
        if (fioValue && fioValue !== 'Покупатель' && fioValue !== 'Buyer' && !fioValue.includes('Товар') && !fioValue.includes('Email') && !fioValue.includes('Телефон')) {
          buyer = fioValue;
        }
      }
    }


    if (!buyer) {
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('Покупатель:')) {
          const buyerName = trimmedLine.replace('Покупатель:', '').trim();
          if (buyerName && buyerName !== 'Покупатель' && buyerName !== 'Buyer') {
            buyer = buyerName;
            break;
          }
        }
      }
    }

    return { buyer, supplier };
  };


  const extractOrderName = (description: string) => {
    if (!description) return null;

    const lines = description.split('\n');

    for (const line of lines) {
      if (line.includes('НАЗВАНИЕ ЗАКАЗА:')) {
        return line.split('НАЗВАНИЕ ЗАКАЗА:')[1]?.trim() || '';
      }
    }

    return null;
  };


  const translateStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'created': 'Создан',
      'pending': 'Ожидает оплаты',
      'confirmed': 'Подтвержден',
      'paid': 'Оплачен',
      'success': 'Успешно',
      'failed': 'Ошибка',
      'cancelled': 'Отменен',
      'refunded': 'Возвращен',
      'processing': 'Обрабатывается',
      'authorized': 'Авторизован',
      'declined': 'Отклонен',
      'expired': 'Истек'
    };

    const upperStatus = status.toUpperCase();
    return statusMap[upperStatus] || statusMap[status.toLowerCase()] || status;
  };


  const renderDescription = (text?: string) => {
    if (!text) return <em>—</em>;

    const lines = String(text).split('\n').filter(line => line.trim().length > 0);

    return (
      <div className={styles.descriptionContainer}>
        {lines.map((line, index) => {
          const trimmedLine = line.trim();


          if (trimmedLine.startsWith('Заказ') && trimmedLine.includes('товаров')) {
            return (
              <div key={index} className={styles.orderHeader}>
                <strong>{trimmedLine}</strong>
              </div>
            );
          }


          if (trimmedLine.includes('предоплаты') || trimmedLine.includes('бонусной карты') || trimmedLine.includes('к доплате')) {
            return (
              <div key={index} className={styles.paymentInfo}>
                <span className={styles.paymentLabel}>{trimmedLine.split(':')[0]}:</span>
                <span className={styles.paymentValue}>{trimmedLine.split(':')[1]?.trim()}</span>
              </div>
            );
          }


          if (trimmedLine.startsWith('Поставщик:') || trimmedLine.startsWith('Покупатель:')) {
            return (
              <div key={index} className={styles.partyInfo}>
                <span className={styles.partyLabel}>{trimmedLine.split(':')[0]}:</span>
                <span className={styles.partyValue}>{trimmedLine.split(':')[1]?.trim()}</span>
              </div>
            );
          }


          if (trimmedLine.startsWith('Товар') && trimmedLine.includes(':')) {
            return (
              <div key={index} className={styles.productHeader}>
                <strong>{trimmedLine}</strong>
              </div>
            );
          }


          if (trimmedLine.startsWith('  ') && trimmedLine.includes(':')) {
            const cleanLine = trimmedLine.trim();
            const [label, value] = cleanLine.split(':');
            return (
              <div key={index} className={styles.productProperty}>
                <span className={styles.propertyLabel}>{label}:</span>
                <span className={styles.propertyValue}>{value?.trim() || '—'}</span>
              </div>
            );
          }


          return (
            <div key={index} className={styles.descriptionLine}>
              {trimmedLine}
            </div>
          );
        })}
      </div>
    );
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;


    setUploadedFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const rows = jsonData as any[][];
        const items: ExcelRow[] = [];
        let totalAmount = 0;
        let supplierName = '';
        let buyerName = '';
        let documentNumber = '';
        let documentDate = '';


        let headerDetected = false;
        let colIndex: Record<string, number> = {};

        const parseNumber = (value: any): number => {
          if (typeof value === 'number') return value;
          if (typeof value === 'string') {
            const cleanValue = value.replace(/\s/g, '').replace(',', '.');
            return parseFloat(cleanValue) || 0;
          }
          return 0;
        };

        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length === 0) continue;


          for (let c = 0; c < row.length; c++) {
            const cell = row[c];
            if (typeof cell === 'string') {
              const lower = cell.toLowerCase();

              if (!documentNumber && lower.includes('заказ клиента')) {
                const numMatch = cell.match(/№\s*([^\s]+(?:\s+[^\s]+)?)\s+от/i);
                if (numMatch) documentNumber = numMatch[1].trim();
                const dateMatch = cell.match(/от\s+(.+)$/i);
                if (dateMatch) documentDate = dateMatch[1].replace(/\s*г\.?\s*$/i, '').trim();
              }

              if (!supplierName && (lower.includes('поставщик') || lower.includes('исполнитель'))) {
                const after = cell.includes(':') ? cell.split(':')[1].trim() : '';
                supplierName = after || (row[c + 1] ? String(row[c + 1]).trim() : '');
                console.log('[DEBUG] Found supplier:', supplierName);
              }

              if (!buyerName && (lower.includes('покупатель') || lower.includes('заказчик') || lower.includes('клиент'))) {
                const after = cell.includes(':') ? cell.split(':').slice(1).join(':').trim() : '';
                buyerName = after || (row[c + 1] ? String(row[c + 1]).trim() : '');
                console.log('[DEBUG] Found buyer:', buyerName);
              }

              if (!buyerName && cell.length > 10 && cell.length < 100 &&
                  !lower.includes('поставщик') && !lower.includes('товар') &&
                  !lower.includes('№') && !lower.includes('количество') &&
                  !lower.includes('цена') && !lower.includes('сумма') &&
                  (cell.includes(' ') || cell.includes('.')) &&
                  c > 0 && c < row.length - 1) {

                const prevCell = row[c - 1] ? String(row[c - 1]).toLowerCase() : '';
                const nextCell = row[c + 1] ? String(row[c + 1]).toLowerCase() : '';
                if (prevCell.includes('покупатель') || nextCell.includes('покупатель') ||
                    prevCell.includes('заказчик') || nextCell.includes('заказчик')) {
                  buyerName = cell.trim();
                  console.log('[DEBUG] Found buyer by context:', buyerName);
                }
              }
            }
          }


          if (!headerDetected) {
            const textRow = row.map((x: any) =>
              typeof x === 'string' ? normalizeHeaderCell(x) : x
            );
            console.log('[DEBUG] Checking row for header:', textRow);

            const hasNum = textRow.some(
              (v: any) => v === '№' || v === 'no' || normalizeHeaderCell(v) === '№'
            );
            const hasProduct = findProductColumnIndex(textRow as string[]) >= 0;
            const hasQuantity = textRow.some(
              (v: any) => String(v).includes('количество') || String(v).includes('кол-во')
            );
            const hasPrice = textRow.some(
              (v: any) => String(v).startsWith('цена') || String(v).includes('цена')
            );
            const hasSum = findSumColumnIndex(textRow as string[]) >= 0;

            console.log('[DEBUG] Header check:', { hasNum, hasProduct, hasQuantity, hasPrice, hasSum });

            if (hasNum && hasProduct && hasQuantity && hasPrice && hasSum) {
              headerDetected = true;
              colIndex = {
                num: textRow.findIndex(
                  (v: any) => v === '№' || v === 'no' || normalizeHeaderCell(v) === '№'
                ),
                name: findProductColumnIndex(textRow as string[]),
                qty: textRow.findIndex(
                  (v: any) => String(v).includes('количество') || String(v).includes('кол-во')
                ),
                price: textRow.findIndex(
                  (v: any) => String(v).startsWith('цена') || String(v).includes('цена')
                ),
                sum: findSumColumnIndex(textRow as string[]),
              };
              console.log('[DEBUG] Header detected, column indices:', colIndex);
              continue;
            }
          } else {

            if (rowLooksLikeTotal(row)) {
              const sumCol = colIndex.sum;
              const totalCell = row[sumCol];
              const parsedTotal = parseNumber(totalCell);
              if (parsedTotal > 0) totalAmount = parsedTotal;
              console.log('[DEBUG] Found total row, stopping item parsing');
              break;
            }

            const numCell = row[colIndex.num];
            const nameCell = row[colIndex.name];
            const qtyCell = row[colIndex.qty];
            const priceCell = row[colIndex.price];
            const sumCell = row[colIndex.sum];


            if (nameCell === undefined || nameCell === null || String(nameCell).trim() === '') {
              continue;
            }

            const item: ExcelRow = {
              '№': parseNumber(numCell),
              'Товар': String(nameCell || ''),
              'Серия': '',
              'Шир': 0,
              'Состав': '',
              'Пр-во': '',
              'Количество': parseNumber(qtyCell),
              'Цена, руб': parseNumber(priceCell),
              'Сумма, руб': parseNumber(sumCell),
            };

            console.log('[DEBUG] Parsed item:', item);


            if (!item['Сумма, руб'] && !item['Количество'] && !item['Цена, руб']) continue;
            items.push(item);
            totalAmount += item['Сумма, руб'];
            continue;
          }


          if (!headerDetected && row[0] && row[2] && String(row[2]).trim().length > 0) {
            const item: ExcelRow = {
              '№': parseNumber(row[0]),
              'Товар': String(row[2] || ''),
              'Серия': String(row[18] || ''),
              'Шир': parseNumber(row[20]),
              'Состав': String(row[22] || ''),
              'Пр-во': String(row[28] || ''),
              'Количество': parseNumber(row[32]),
              'Цена, руб': parseNumber(row[37]),
              'Сумма, руб': parseNumber(row[43]),
            };
            items.push(item);
            totalAmount += item['Сумма, руб'];
          }
        }

        let buyerDisplay = buyerName || 'Покупатель';
        let buyerPhone = '';
        if (buyerName) {
          const phoneMatch = buyerName.match(/тел\.?\s*:?\s*([\d+\s()-]+)/i);
          if (phoneMatch) {
            buyerPhone = phoneMatch[1].replace(/\D/g, '');
            buyerDisplay = buyerName.split(/,?\s*тел\.?\s*:?/i)[0].trim();
          }
        }

        if (items.length === 0) {
          setError(
            'Не удалось распознать товары в файле. Поддерживаются таблицы с колонками №, Товар(ы), Количество, Цена и Сумма (в т.ч. формат «Заказ клиента» из 1С).'
          );
          setExcelData(null);
          setEditableItems([]);
          return;
        }

        const orderLabel =
          documentNumber && documentDate
            ? `Заказ клиента № ${documentNumber} от ${documentDate}`
            : documentNumber
              ? `Заказ клиента № ${documentNumber}`
              : file.name.replace(/\.(xlsx|xls)$/i, '');

        setUploadedFileName(orderLabel);

        setExcelData({
          supplier: supplierName || 'Табанакова Владлена Николаевна',
          buyer: buyerDisplay,
          totalAmount: totalAmount || items.reduce((s, it) => s + (it['Сумма, руб'] || 0), 0),
          items,
          documentNumber,
          documentDate,
        });

        setEditableItems([...items]);
        if (buyerDisplay && buyerDisplay !== 'Покупатель') {
          setCustomerFullName(buyerDisplay);
        }
        if (buyerPhone) {
          setCustomerPhone(formatPhoneInput(buyerPhone));
        }
        setError(null);
      } catch (err) {
        console.error('Error parsing Excel:', err);
        setError('Ошибка при парсинге файла');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleItemChange = (index: number, field: keyof ExcelRow, value: string | number) => {
    const newItems = [...editableItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditableItems(newItems);
  };

  const handleRemoveItem = (index: number) => {
    setEditableItems(editableItems.filter((_, i) => i !== index));
  };

  const handleAddItem = () => {
    const newItem: ExcelRow = {
      '№': editableItems.length + 1,
      'Товар': '',
      'Серия': '',
      'Шир': 0,
      'Состав': '',
      'Пр-во': '',
      'Количество': 1,
      'Цена, руб': 0,
      'Сумма, руб': 0,
    };
    setEditableItems([...editableItems, newItem]);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>, setPhone: (value: string) => void) => {
    setPhone(formatPhoneInput(e.target.value));
  };


  const handleCreatePrepayment = async () => {

    if (!prepaymentEmail || !prepaymentPhone || !prepaymentAmount) {
      setError('Заполните все обязательные поля');
      return;
    }


    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(prepaymentEmail)) {
      setError('Введите корректный email');
      return;
    }

    const phoneError = validatePhone(prepaymentPhone);
    if (phoneError) {
      setError(phoneError);
      return;
    }


    const amount = parseFloat(prepaymentAmount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      setError('Введите корректную сумму предоплаты');
      return;
    }

    if (!prepaymentConsent) {
      setError('Необходимо согласие на обработку персональных данных');
      return;
    }

    setIsPrepaymentLoading(true);
    setError(null);
    setPrepaymentUrl(null);

    try {
      const token = localStorage.getItem("token");

      let description = `Авансовый платеж на сумму ${amount.toFixed(2)} руб\n\n`;
      if (prepaymentFullName) {
        description += `ФИО: ${prepaymentFullName}\n`;
      }
      description += `Email: ${prepaymentEmail}\n`;
      description += `Телефон: ${prepaymentPhone}\n\n`;
      description += `Поставщик: ${excelData?.supplier || 'ИП Табанакова Владлена Николаевна'}\n`;
      description += `Покупатель: ${prepaymentFullName || excelData?.buyer || 'Покупатель'}`;

      const requestData = {
        amount: amount,
        description: description,
        full_name: prepaymentFullName,
        email: prepaymentEmail,
        phone: prepaymentPhone,
        consent: prepaymentConsent
      };

      console.log('Creating prepayment...', requestData);

      const res = await axios.post(`${API_BASE_URL}/api/v1/payment/prepayment_create`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('Prepayment response:', res.data);

      if (res.data.payment_url) {
        setPrepaymentUrl(res.data.payment_url);
      } else if (res.data.receipt_id) {
        setPrepaymentUrl(`${window.location.origin}/receipt/${res.data.receipt_id}`);
      }
    } catch (err: any) {
      console.error('Prepayment error:', err);
      setError(err.response?.data?.detail || err.message || 'Ошибка при создании предоплаты');
    } finally {
      setIsPrepaymentLoading(false);
    }
  };


  const handleCreatePayment = async () => {

    if (!customerFullName || !customerEmail || !customerPhone) {
      setError('Заполните ФИО, email и телефон клиента');
      return;
    }


    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      setError('Введите корректный email');
      return;
    }

    const phoneError = validatePhone(customerPhone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    if (editableItems.length === 0) {
      setError('Добавьте хотя бы один товар');
      return;
    }

    if (!customerConsent) {
      setError('Необходимо согласие на обработку персональных данных');
      return;
    }

    setIsLoading(true);
    setError(null);
    setPaymentUrl(null);

    try {
      const token = localStorage.getItem("token");
      const totalAmount = Math.round(
        editableItems.reduce((sum, item) => sum + (Number(item['Сумма, руб']) || 0), 0) * 100
      ) / 100;
      const prepaymentAmountValue = parseFloat(linkedPrepaymentAmount.replace(',', '.')) || 0;
      const deliveryAmount = paymentDelivery ? parseFloat(paymentDelivery.replace(',', '.')) : 0;

      if (isNaN(deliveryAmount) || deliveryAmount < 0) {
        setError('Введите корректную стоимость доставки');
        setIsLoading(false);
        return;
      }

      const grossTotal = Math.round((totalAmount + deliveryAmount) * 100) / 100;
      let finalAmount = Math.max(0, Math.round((grossTotal - prepaymentAmountValue) * 100) / 100);


      if (
        selectedOneCItemId &&
        prepaymentAmountValue === 0 &&
        deliveryAmount === 0 &&
        excelData?.totalAmount != null &&
        !Number.isNaN(Number(excelData.totalAmount))
      ) {
        const declared = Math.round(Number(excelData.totalAmount) * 100) / 100;
        if (Math.abs(declared - totalAmount) <= 0.02) {
          finalAmount = declared;
        }
      }

      if (finalAmount <= 0) {
        setError('Сумма к оплате должна быть больше 0');
        setIsLoading(false);
        return;
      }

      console.log('Creating payment...');
      console.log('Total amount (items):', totalAmount);
      console.log('Delivery amount:', deliveryAmount);
      console.log('Prepayment amount:', prepaymentAmountValue);
      console.log('Final amount (to pay):', finalAmount);
      console.log('API URL:', `${API_BASE_URL}/api/v1/payment/simple_create`);

      let description = `Заказ ${editableItems.length} товаров на сумму ${totalAmount.toFixed(2)} руб\n`;
      if (deliveryAmount > 0) {
        description += `Стоимость доставки: ${deliveryAmount.toFixed(2)} руб\n`;
      }

      if (linkedPrepaymentNumber || prepaymentAmountValue > 0) {
        if (linkedPrepaymentNumber) description += `Номер предоплаты: ${linkedPrepaymentNumber}\n`;
        if (prepaymentAmountValue > 0) description += `Сумма предоплаты: ${prepaymentAmountValue.toFixed(2)} руб\n`;
      }

      description += `Итого к доплате: ${finalAmount.toFixed(2)} руб\n`;
      description += `\n`;
      description += `Поставщик: ${excelData?.supplier || 'ИП Табанакова Владлена Николаевна'}\n`;
      description += `Покупатель: ${customerFullName || excelData?.buyer || 'Покупатель'}\n\n`;

      editableItems.forEach((item, index) => {
        description += `Товар ${index + 1}:\n`;
        description += `  Название: ${item['Товар']}\n`;
        description += `  Серия: ${item['Серия']}\n`;
        description += `  Ширина: ${item['Шир']}\n`;
        description += `  Состав: ${item['Состав']}\n`;
        description += `  Производство: ${item['Пр-во']}\n`;
        description += `  Количество: ${item['Количество']}\n`;
        description += `  Цена: ${item['Цена, руб']} руб\n`;
        description += `  Сумма: ${item['Сумма, руб']} руб\n\n`;
      });


      let itemsForReceipt: any[] | undefined = undefined;
      if (selectedOneCItemId && prepaymentAmountValue === 0 && deliveryAmount === 0) {
        itemsForReceipt = editableItems.map((item, idx) => {
          const qtyRaw = Number(item['Количество']);
          const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
          const amount = Math.round((Number(item['Сумма, руб']) || 0) * 100) / 100;
          const name = String(item['Товар'] || '').trim() || `Товар ${idx + 1}`;
          const price = Math.round((amount / qty) * 100) / 100;
          return { name, quantity: qty, price, amount };
        });
      } else if (prepaymentAmountValue > 0 || deliveryAmount > 0) {
        const productsPortion = Math.max(0, totalAmount - prepaymentAmountValue);
        const itemsList: { name: string; quantity: number; price: number; amount: number }[] = [];
        let itemsSum = 0;
        const safeTotal = totalAmount > 0 ? totalAmount : 1;
        editableItems.forEach((item, idx) => {
          const itemSum = Number(item['Сумма, руб']) || 0;
          const qty = Number(item['Количество']) || 1;
          const name = String(item['Товар'] || '').trim() || `Товар ${idx + 1}`;
          let amount: number;
          if (idx < editableItems.length - 1) {
            amount = Math.round((itemSum * (productsPortion / safeTotal)) * 100) / 100;
          } else {
            amount = Math.round((finalAmount - deliveryAmount - itemsSum) * 100) / 100;
          }
          itemsSum += amount;
          itemsList.push({
            name,
            quantity: qty,
            price: qty > 0 ? amount / qty : amount,
            amount,
          });
        });
        if (deliveryAmount > 0) {
          itemsList.push({
            name: 'Доставка',
            quantity: 1,
            price: deliveryAmount,
            amount: deliveryAmount,
          });
        }

        const targetKop = Math.round(finalAmount * 100);
        let sumKop = 0;
        for (const row of itemsList) {
          sumKop += Math.round((Number(row.amount) || 0) * 100);
        }
        const deltaKop = targetKop - sumKop;
        if (deltaKop !== 0 && itemsList.length > 0) {
          const lastRow = itemsList[itemsList.length - 1];
          lastRow.amount = (Math.round((Number(lastRow.amount) || 0) * 100) + deltaKop) / 100;
          const lastQty = Number(lastRow.quantity) || 1;
          lastRow.price = Math.round((lastRow.amount / lastQty) * 100) / 100;
        }
        itemsForReceipt = itemsList;
      }

      const requestData: any = {
        amount: finalAmount,
        description: description,
        email: customerEmail,
        phone: customerPhone,
        full_name: customerFullName,
        order_name: uploadedFileName || `Заказ ${new Date().toLocaleDateString()}`
      };
      if (itemsForReceipt) {
        requestData.items = itemsForReceipt;
      }
      if (selectedOneCItemId) {
        requestData.delete_onec_group_id = selectedOneCItemId;
      }

      console.log('Request data:', requestData);

      const res = await axios.post(`${API_BASE_URL}/api/v1/payment/simple_create`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('Payment response:', res.data);

      if (res.data.payment_url) {
        setPaymentUrl(res.data.payment_url);
      } else if (res.data.receipt_id) {
        setPaymentUrl(`${window.location.origin}/receipt/${res.data.receipt_id}`);
      }

      if (selectedOneCItemId) {
        setSelectedOneCItemId(null);
        loadOneCItems(oneCItemSearch);
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.detail || err.message || "Ошибка при создании оплаты");
    } finally {
      setIsLoading(false);
    }
  };

  const loadHistoryPage = async (page: number) => {
    const requestId = ++historyLoadRequestIdRef.current;
    setIsHistoryLoading(true);
    setHistoryLoadError(null);
    try {
      const token = localStorage.getItem("token");
      const data = await getWithFallback<PaymentHistoryResponse>('/api/v1/payment/history', {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 25000,
        params: {
          page,
          limit: HISTORY_LIMIT,
          search: historySearch.trim() || undefined,
          with_meta: true,
        },
      });
      if (requestId !== historyLoadRequestIdRef.current) return;
      setHistory(data.items || []);
      setHistoryPage(data.page || page);
      setHistoryTotalPages(data.total_pages || 1);
      setHistoryTotal(data.total || 0);
    } catch (err: any) {
      if (requestId !== historyLoadRequestIdRef.current) return;
      const message =
        err.code === 'ECONNABORTED'
          ? 'История оплат не успела загрузиться (таймаут). Нажмите «Повторить загрузку».'
          : err.response?.data?.detail || 'Ошибка при загрузке истории оплат';
      setError(message);
      setHistoryLoadError(message);
      setHistory([]);
    } finally {
      if (requestId === historyLoadRequestIdRef.current) {
        setIsHistoryLoading(false);
      }
    }
  };

  const handleHistoryClick = () => {
    setShowHistory((prev) => !prev);
  };

  const handleRefreshStatuses = async () => {
    if (!history || history.length === 0) return;
    setIsRefreshing(true);
    try {
      const token = localStorage.getItem("token");
      await Promise.all(
        history.map(async (item) => {
          try {

            await axios.get(`${API_BASE_URL}/api/v1/payment/status/${item.id}`, {
              headers: { Authorization: `Bearer ${token}` },
              params: { force_refresh: true }
            });
          } catch (err) {
            console.error(`Failed to refresh status for order ${item.id}:`, err);
          }
        })
      );


      await loadHistoryPage(historyPage);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Ошибка при обновлении статусов оплат");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFetchReceipt = async (orderId: number) => {
    if (fetchingReceipts[orderId]) return;

    setFetchingReceipts(prev => ({ ...prev, [orderId]: true }));
    try {
      const token = localStorage.getItem("token");


      await axios.post(`${API_BASE_URL}/api/v1/payment/fetch_receipt_for_order/${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });


      await loadHistoryPage(historyPage);

    } catch (err: any) {
      console.error('Error fetching receipt:', err);
      setError(err.response?.data?.detail || 'Ошибка при получении чека');
    } finally {
      setFetchingReceipts(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleDownloadReceipt = async (orderId: number) => {
    try {
      const token = localStorage.getItem("token");
      const historyItem = history?.find(item => item.id === orderId);

      if (!historyItem || !historyItem.receipt) {
        setError('Чек не найден');
        return;
      }


      const downloadUrl = `${API_BASE_URL}/api/v1/payment/receipt_pdf/${historyItem.receipt.id}?token=${token}`;


      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `receipt_${historyItem.receipt.id}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error('Error downloading receipt:', err);
      setError('Ошибка при скачивании чека');
    }
  };

  const handleGetATOLReceiptLink = async (orderId: number) => {
    try {
      setFetchingReceiptLinks(prev => ({ ...prev, [orderId]: true }));

      const token = localStorage.getItem("token");
      const historyItem = history?.find(item => item.id === orderId);

      if (!historyItem) {
        setError('Заказ не найден');
        return;
      }


      if (!historyItem.receipt) {
        setError('Чек не найден. Сначала создайте чек через обычную систему.');
        return;
      }


      if (!historyItem.receipt.payment_id) {
        setError('У этого заказа нет чека ATOL. Сначала создайте чек.');
        return;
      }

      const atolUuid = historyItem.receipt.payment_id;


      const response = await axios.get(
        `${API_BASE_URL}/api/v1/atol/receipt_link/${atolUuid}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.receipt_url) {

        setHistory(prev =>
          prev?.map(item =>
            item.id === orderId
              ? { ...item, receipt_url: response.data.receipt_url }
              : item
          ) || null
        );


        window.open(response.data.receipt_url, '_blank');
      } else {
        setError('Ссылка на чек пока не готова. Попробуйте позже.');
      }
    } catch (err: any) {
      console.error('Error getting ATOL receipt link:', err);
      setError('Ошибка при получении ссылки на чек: ' + (err.response?.data?.detail || err.message));
    } finally {
      setFetchingReceiptLinks(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleCreateATOLReceipt = async (orderId: number) => {
    try {
      setFetchingReceiptLinks(prev => ({ ...prev, [orderId]: true }));

      const token = localStorage.getItem("token");
      const historyItem = history?.find(item => item.id === orderId);

      if (!historyItem) {
        setError('Заказ не найден');
        return;
      }


      const response = await axios.post(
        `${API_BASE_URL}/api/v1/atol/create_receipt`,
        {
          order_id: orderId.toString(),
          amount: historyItem.total_amount,
          items: [
            {
              name: extractOrderName(historyItem.description || '') || `Заказ #${orderId}`,
              price: historyItem.total_amount,
              quantity: 1,
              amount: historyItem.total_amount
            }
          ],
          email: historyItem.email,
          phone: '+79991234567'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.uuid) {
        setError(`Чек ATOL создан успешно! UUID: ${response.data.uuid}`);


        await loadHistoryPage(historyPage);


        setTimeout(() => {
          setError('Теперь можете получить ссылку на чек');
        }, 2000);
      } else {
        setError('Ошибка при создании чека ATOL');
      }
    } catch (err: any) {
      console.error('Error creating ATOL receipt:', err);
      setError('Ошибка при создании чека ATOL: ' + (err.response?.data?.detail || err.message));
    } finally {
      setFetchingReceiptLinks(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleFetchAllReceipts = async () => {
    try {
      const token = localStorage.getItem("token");
      setError('');

      const response = await axios.post(`${API_BASE_URL}/api/v1/payment/fetch_all_receipts`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('Bulk fetch results:', response.data);


      await loadHistoryPage(historyPage);


      const successCount = response.data.results.filter((r: any) => r.status === 'success').length;
      setError(`Обновлено чеков: ${successCount} из ${response.data.processed}`);

    } catch (err: any) {
      console.error('Error updating receipts:', err);
      setError(err.response?.data?.detail || 'Ошибка при обновлении чеков');
    }
  };

  const handleRefundOrder = async (orderId: number) => {
    if (refundingOrders[orderId]) return;

    if (!window.confirm('Вы уверены, что хотите выполнить возврат оплаты по этому заказу?')) {
      return;
    }

    setRefundingOrders(prev => ({ ...prev, [orderId]: true }));
    setError(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/payment/refund/${orderId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );


      await loadHistoryPage(historyPage);


      setSuccessMessage('Возврат оплаты успешно выполнен!');

      setTimeout(() => setSuccessMessage(null), 5000);


      setIsHistoryModalOpen(false);
      setSelectedHistoryItem(null);
    } catch (err: any) {
      console.error('Error refunding order:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Ошибка при возврате оплаты';
      setError(errorMessage);

      setTimeout(() => setError(null), 7000);
    } finally {
      setRefundingOrders(prev => ({ ...prev, [orderId]: false }));
    }
  };


  const handleSaveCustomer = async () => {
    if (!newCustomerFullName.trim()) {
      setError('Введите ФИО клиента');
      return;
    }

    setIsSavingCustomer(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (editingCustomer) {

        await axios.put(
          `${API_BASE_URL}/api/v1/customers/${editingCustomer.id}`,
          {
            full_name: newCustomerFullName,
            phone: newCustomerPhone || null,
            email: newCustomerEmail || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccessMessage('Клиент успешно обновлен!');
      } else {

        await axios.post(
          `${API_BASE_URL}/api/v1/customers/`,
          {
            full_name: newCustomerFullName,
            phone: newCustomerPhone || null,
            email: newCustomerEmail || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccessMessage('Клиент успешно создан!');
      }


      await loadCustomers(customerSearch);


      setNewCustomerFullName('');
      setNewCustomerPhone('');
      setNewCustomerEmail('');
      setEditingCustomer(null);
      setShowCustomerForm(false);

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error saving customer:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Ошибка при сохранении клиента';
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSavingCustomer(false);
    }
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setNewCustomerFullName(customer.full_name);
    setNewCustomerPhone(customer.phone || '');
    setNewCustomerEmail(customer.email || '');
    setShowCustomerForm(true);
  };

  const handleDeleteCustomer = async (customerId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого клиента?')) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/v1/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMessage('Клиент успешно удален!');
      await loadCustomers(customerSearch);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error deleting customer:', err);
      setError('Ошибка при удалении клиента');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSelectCustomer = (customerId: number | null, isPrepayment: boolean = false) => {
    if (isPrepayment) {
      setSelectedPrepaymentCustomerId(customerId);
      if (customerId) {
        const customer = customers.find(c => c.id === customerId);
        if (customer) {
          setPrepaymentFullName(customer.full_name);
          setPrepaymentEmail(customer.email || '');
          setPrepaymentPhone(customer.phone || '');
        }
      } else {
        setPrepaymentFullName('');
        setPrepaymentEmail('');
        setPrepaymentPhone('');
      }
    } else {
      setSelectedCustomerId(customerId);
      if (customerId) {
        const customer = customers.find(c => c.id === customerId);
        if (customer) {
          setCustomerFullName(customer.full_name);
          setCustomerEmail(customer.email || '');
          setCustomerPhone(customer.phone || '');
        }
      } else {
        setCustomerFullName('');
        setCustomerEmail('');
        setCustomerPhone('');
      }
    }
  };


  const handleSaveManager = async () => {
    if (!managerEmail.trim()) {
      setError('Введите email менеджера');
      return;
    }


    if (!editingManager && !managerPassword.trim()) {
      setError('Введите пароль для нового менеджера (минимум 8 символов)');
      return;
    }

    if (!editingManager && managerPassword.length < 8) {
      setError('Пароль должен содержать минимум 8 символов');
      return;
    }

    setIsSavingManager(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (editingManager) {

        await axios.put(
          `${API_BASE_URL}/api/v1/users/managers/${editingManager.id}`,
          {
            email: managerEmail,
            full_name: managerFullName || null,
            phone: managerPhone || null,
            is_active: managerIsActive,
            is_verified: managerIsVerified,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccessMessage('Менеджер успешно обновлен!');
      } else {

        await axios.post(
          `${API_BASE_URL}/api/v1/users/managers`,
          {
            email: managerEmail,
            password: managerPassword,
            full_name: managerFullName || null,
            phone: managerPhone || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccessMessage('Менеджер успешно создан!');
      }


      await loadManagers();


      setManagerEmail('');
      setManagerFullName('');
      setManagerPhone('');
      setManagerPassword('');
      setManagerIsActive(true);
      setManagerIsVerified(true);
      setEditingManager(null);
      setShowManagerForm(false);

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error saving manager:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Ошибка при сохранении менеджера';
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSavingManager(false);
    }
  };

  const handleEditManager = (manager: Manager) => {
    setEditingManager(manager);
    setManagerEmail(manager.email);
    setManagerFullName(manager.full_name || '');
    setManagerPhone(manager.phone || '');
    setManagerPassword('');
    setManagerIsActive(manager.is_active);
    setManagerIsVerified(manager.is_verified);
    setShowManagerForm(true);
  };

  const handleDeleteManager = async (managerId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого менеджера?')) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/v1/users/managers/${managerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMessage('Менеджер успешно удален!');
      await loadManagers();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error deleting manager:', err);
      setError('Ошибка при удалении менеджера');
      setTimeout(() => setError(null), 5000);
    }
  };

  console.log('Rendering JSX...');
  const originalTotal = editableItems.reduce((sum, item) => sum + item['Сумма, руб'], 0);


  const prepaymentAmountValue = parseFloat(linkedPrepaymentAmount.replace(',', '.')) || 0;
  const deliveryAmountValue = paymentDelivery ? parseFloat(paymentDelivery.replace(',', '.')) || 0 : 0;


  const finalAmount = Math.max(0, originalTotal + deliveryAmountValue - prepaymentAmountValue);

  const filteredHistory = history
    ? [...history].sort((a, b) => {
        const timeA = new Date(a.created_at).getTime();
        const timeB = new Date(b.created_at).getTime();
        return historySortDirection === 'asc' ? timeA - timeB : timeB - timeA;
      })
    : [];

  const openHistoryModal = (item: PaymentHistoryItem) => {
    setSelectedHistoryItem(item);
    setIsHistoryModalOpen(true);
  };

  const closeHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedHistoryItem(null);
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.paymentWrapper}>
          <h2 className={styles.paymentTitle}>Создать оплату</h2>
          <button onClick={handleHistoryClick} className={styles.historyBtn}>История оплат</button>

          {showHistory && (
            <div className={styles.historySection}>
              <h3 className={styles.historySectionTitle}>История оплат</h3>
              <div className={styles.historyPaginationTop}>
                <span>
                  Страница {historyPage} из {historyTotalPages} (всего записей: {historyTotal})
                </span>
              </div>
              <div className={styles.historyControls}>
                <div className={styles.historyControlsLeft}>
                  <button
                    onClick={handleRefreshStatuses}
                    disabled={isRefreshing}
                    className={styles.historyControlButton}
                  >
                    {isRefreshing ? 'Обновление…' : 'Обновить статусы'}
                  </button>
                  <button
                    onClick={handleFetchAllReceipts}
                    className={`${styles.historyControlButton} ${styles.historyControlButtonGreen}`}
                  >
                    🔄 Обновить чеки
                  </button>
                </div>
                <div className={styles.historyControlsRight}>
                  <input
                    type="text"
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    placeholder="Поиск по названию заказа или клиенту..."
                    className={styles.historySearchInput}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setHistorySortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'))
                    }
                    className={styles.historySortButton}
                  >
                    {historySortDirection === 'desc' ? 'По дате ↓' : 'По дате ↑'}
                  </button>
                </div>
              </div>

              {isHistoryLoading && <div className={styles.historyEmpty}>Загрузка истории...</div>}
              {!isHistoryLoading && historyLoadError && (
                <div className={styles.historyEmpty}>
                  <div>{historyLoadError}</div>
                  <button
                    type="button"
                    className={styles.historyPageButton}
                    onClick={() => loadHistoryPage(historyPage || 1)}
                    style={{ marginTop: '10px' }}
                  >
                    Повторить загрузку
                  </button>
                </div>
              )}
              {!isHistoryLoading && !historyLoadError && filteredHistory.length === 0 && <div className={styles.historyEmpty}>Нет оплат</div>}

              {!isHistoryLoading && !historyLoadError && filteredHistory.length > 0 && (
                <div className={styles.historyTableWrapper}>
                  <table className={styles.historyTable}>
                    <thead>
                      <tr>
                        <th>№</th>
                        <th>Заказ</th>
                        <th>Покупатель</th>
                        <th>Поставщик</th>
                        <th>Сумма</th>
                        <th>Статус</th>
                        <th>Дата</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((item) => {
                        const isConfirmed = ['confirmed', 'paid', 'success'].includes(item.status.toLowerCase());
                        const { buyer, supplier } = extractBuyerAndSupplier(item.description || '');
                        const orderTitle = extractOrderName(item.description || '') || `Заказ #${item.id}`;

                        return (
                          <tr
                            key={item.id}
                            className={styles.historyRow}
                            onDoubleClick={() => openHistoryModal(item)}
                          >
                            <td className={styles.historyCell}>{item.id}</td>
                            <td className={styles.historyCell}>{orderTitle}</td>
                            <td className={styles.historyCell}>{buyer || <em>—</em>}</td>
                            <td className={styles.historyCell}>{supplier || <em>—</em>}</td>
                            <td className={styles.historyCell}>{formatCurrency(item.total_amount)}</td>
                            <td className={styles.historyCell}>
                              <span className={`${styles.status} ${isConfirmed ? styles.statusConfirmed : styles.statusFailed}`}>
                                {translateStatus(item.status)}
                              </span>
                            </td>
                            <td className={styles.historyCell}>
                              {new Date(item.created_at).toLocaleString('ru-RU')}
                            </td>
                            <td className={styles.historyCell}>{item.email || <em>—</em>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className={styles.historyHint}>
                    Дважды кликните по строке, чтобы открыть подробную информацию по оплате.
                  </div>
                </div>
              )}
              <div className={styles.historyPagination}>
                <button
                  type="button"
                  className={styles.historyPageButton}
                  onClick={() => loadHistoryPage(historyPage - 1)}
                  disabled={isHistoryLoading || historyPage <= 1}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className={styles.historyPageButton}
                  onClick={() => loadHistoryPage(historyPage + 1)}
                  disabled={isHistoryLoading || historyPage >= historyTotalPages}
                >
                  Вперед
                </button>
              </div>
            </div>
          )}

          {isHistoryModalOpen && selectedHistoryItem && (
            <div className={styles.historyModalOverlay} onClick={closeHistoryModal}>
              <div className={styles.historyModal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.historyModalHeader}>
                  <h3 className={styles.historyModalTitle}>
                    {extractOrderName(selectedHistoryItem.description || '') || `Заказ #${selectedHistoryItem.id}`}
                  </h3>
                  <button className={styles.historyModalClose} onClick={closeHistoryModal}>
                    ✕
                  </button>
                </div>

                <div className={styles.historyModalBody}>
                  <div className={styles.historyContent}>
                    <div className={styles.historyMainInfo}>
                      <div className={styles.historyInfoRow}>
                        <div className={styles.historyLabel}>Сумма</div>
                        <div className={styles.historyValue}>{formatCurrency(selectedHistoryItem.total_amount)}</div>
                      </div>
                      <div className={styles.historyInfoRow}>
                        <div className={styles.historyLabel}>Дата</div>
                        <div className={styles.historyValue}>{new Date(selectedHistoryItem.created_at).toLocaleString('ru-RU')}</div>
                      </div>
                      <div className={styles.historyInfoRow}>
                        <div className={styles.historyLabel}>Email</div>
                        <div className={styles.historyValue}>{selectedHistoryItem.email || <em>—</em>}</div>
                      </div>
                    </div>

                    <div className={styles.historyPeopleInfo}>
                      {extractBuyerAndSupplier(selectedHistoryItem.description || '').buyer && (
                        <div className={styles.historyInfoRow}>
                          <div className={styles.historyLabel}>Покупатель</div>
                          <div className={styles.historyValue}>
                            {extractBuyerAndSupplier(selectedHistoryItem.description || '').buyer}
                          </div>
                        </div>
                      )}
                      {extractBuyerAndSupplier(selectedHistoryItem.description || '').supplier && (
                        <div className={styles.historyInfoRow}>
                          <div className={styles.historyLabel}>Поставщик</div>
                          <div className={styles.historyValue}>
                            {extractBuyerAndSupplier(selectedHistoryItem.description || '').supplier}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={styles.historyDescription}>
                      <div className={styles.historyLabel}>Описание</div>
                      <div className={styles.historyValue}>{renderDescription(selectedHistoryItem.description)}</div>
                    </div>
                  </div>

                  <div className={styles.historyModalActions}>
                    {selectedHistoryItem.receipt?.has_pdf ? (
                      <button
                        onClick={() => handleDownloadReceipt(selectedHistoryItem.id)}
                        className={styles.historyActionButton}
                      >
                        📄 Скачать чек
                      </button>
                    ) : (
                      <button
                        onClick={() => handleFetchReceipt(selectedHistoryItem.id)}
                        disabled={fetchingReceipts[selectedHistoryItem.id]}
                        className={styles.historyActionButton}
                      >
                        {fetchingReceipts[selectedHistoryItem.id] ? 'Загрузка...' : 'Загрузить чек'}
                      </button>
                    )}

                    {['confirmed', 'paid', 'success'].includes(selectedHistoryItem.status.toLowerCase()) && (
                      <>
                        {selectedHistoryItem.receipt_url ? (
                          <button
                            onClick={() => window.open(selectedHistoryItem.receipt_url, '_blank')}
                            className={styles.historyActionButtonPurple}
                          >
                            🔗 Открыть чек ATOL
                          </button>
                        ) : (
                          <button
                            onClick={() => handleGetATOLReceiptLink(selectedHistoryItem.id)}
                            disabled={fetchingReceiptLinks[selectedHistoryItem.id]}
                            className={styles.historyActionButtonOrange}
                          >
                            {fetchingReceiptLinks[selectedHistoryItem.id] ? 'Загрузка...' : '🔗 Получить чек ATOL'}
                          </button>
                        )}

                        {!selectedHistoryItem.receipt_url && (
                          <button
                            onClick={() => handleCreateATOLReceipt(selectedHistoryItem.id)}
                            disabled={fetchingReceiptLinks[selectedHistoryItem.id]}
                            className={styles.historyActionButtonCyan}
                          >
                            {fetchingReceiptLinks[selectedHistoryItem.id] ? 'Создание...' : '📄 Создать чек ATOL'}
                          </button>
                        )}
                      </>
                    )}

                    {!['refunded', 'REFUNDED'].includes(selectedHistoryItem.status) && (
                      <button
                        onClick={() => handleRefundOrder(selectedHistoryItem.id)}
                        disabled={!!refundingOrders[selectedHistoryItem.id]}
                        className={styles.historyActionButton}
                      >
                        {refundingOrders[selectedHistoryItem.id] ? 'Возврат...' : '↩️ Возврат оплаты'}
                      </button>
                    )}
                  </div>
                </div>

                {process.env.NODE_ENV === 'development' && (
                  <div className={styles.historyModalDebug}>
                    Debug: isConfirmed={
                      ['confirmed', 'paid', 'success'].includes(selectedHistoryItem.status.toLowerCase()).toString()
                    }, hasReceipt={!!selectedHistoryItem.receipt}, paymentId={
                      selectedHistoryItem.receipt?.payment_id || 'none'
                    }, status={selectedHistoryItem.status}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={styles.prepaymentSection}>
            <h2 className={styles.sectionTitle}>Создание предоплаты</h2>
            <div className={styles.formCard}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>Выбрать клиента</label>
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Поиск по ФИО или E-mail"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px', marginBottom: 8 }}
                />
                <select
                  size={5}
                  value={selectedPrepaymentCustomerId || ''}
                  onChange={(e) => handleSelectCustomer(e.target.value ? parseInt(e.target.value) : null, true)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px', minHeight: '140px', boxSizing: 'border-box' }}
                >
                  <option value="">-- Выберите клиента или введите данные вручную --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.full_name} {customer.phone ? `(${customer.phone})` : ''} {customer.email ? `[${customer.email}]` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>ФИО</label>
                  <input
                    type="text"
                    value={prepaymentFullName}
                    onChange={(e) => {
                      setPrepaymentFullName(e.target.value);
                      setSelectedPrepaymentCustomerId(null);
                    }}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4 }}
                    placeholder="Иванов Иван Иванович"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>
                    E-mail <span style={{color: 'red'}}>*</span>
                  </label>
                  <input
                    type="email"
                    value={prepaymentEmail}
                    onChange={(e) => {
                      setPrepaymentEmail(e.target.value);
                      setSelectedPrepaymentCustomerId(null);
                    }}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4 }}
                    placeholder="name@example.com"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>
                    Телефон <span style={{color: 'red'}}>*</span>
                  </label>
                  <input
                    type="tel"
                    value={prepaymentPhone}
                    onChange={(e) => {
                      handlePhoneChange(e, setPrepaymentPhone);
                      setSelectedPrepaymentCustomerId(null);
                    }}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4 }}
                    placeholder="+7 / +375 / +994"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>
                    Сумма предоплаты (руб) <span style={{color: 'red'}}>*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={prepaymentAmount}
                    onChange={(e) => setPrepaymentAmount(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4 }}
                    placeholder="0"
                    required
                  />
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '16px' }}>
                <input
                  type="checkbox"
                  checked={prepaymentConsent}
                  onChange={(e) => setPrepaymentConsent(e.target.checked)}
                />
                <span>
                  Согласен(а) с <Link to="/policy">политикой обработки персональных данных</Link>
                </span>
              </label>

              <button
                onClick={handleCreatePrepayment}
                className={styles.button}
                disabled={isPrepaymentLoading}
                style={{ width: '100%' }}
              >
                {isPrepaymentLoading ? 'Создание предоплаты...' : 'Создать предоплату'}
              </button>
            </div>
          </div>

          {prepaymentUrl && (
            <div className={styles.qr} style={{ marginBottom: '24px' }}>
              <h4>Ссылка на предоплату</h4>
              <div className={styles.link}>
                <a href={prepaymentUrl} target="_blank" rel="noopener noreferrer">{prepaymentUrl}</a>
              </div>
              <div style={{ marginTop: 16 }}>
                <QRCodeSVG value={prepaymentUrl} size={200} />
              </div>
            </div>
          )}

          <div className={styles.mainPaymentSection}>
            <h2 className={styles.sectionTitle}>Создание основной оплаты</h2>

            {(isManager || isAdmin) && (
              <div className={styles.excelUpload} style={{ marginBottom: '24px' }}>
                <h3 className={styles.subsectionTitle}>Выбор записи из 1С</h3>
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    value={oneCItemSearch}
                    onChange={(e) => setOneCItemSearch(e.target.value)}
                    placeholder="Поиск по номеру документа (например КОУП-000005)"
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>Выбрать запись из 1С</label>
                  <select
                    size={5}
                    value={selectedOneCItemId || ''}
                    onChange={(e) => {
                      const itemId = e.target.value ? parseInt(e.target.value) : null;
                      setSelectedOneCItemId(itemId);
                      if (itemId) {

                        setExcelData(null);
                        setUploadedFileName(null);

                        const fileInput = document.getElementById('excel-file-input') as HTMLInputElement;
                        if (fileInput) {
                          fileInput.value = '';
                        }
                        loadOneCItemDetail(itemId);
                      } else {

                        setExcelData(null);
                        setEditableItems([]);
                        setUploadedFileName(null);
                      }
                    }}
                    disabled={isLoadingOneCItems}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: '16px', lineHeight: 1.4, minHeight: '140px', boxSizing: 'border-box' }}
                  >
                    <option value="">-- Выберите запись из 1С или загрузите Excel файл --</option>
                    {oneCItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.order_name || 'Без названия'} | {item.full_name || 'Без ФИО'} | {item.email || 'Без email'} | {item.phone || 'Без телефона'} | {item.amount?.toFixed(2) || '0.00'} руб ({item.items_count} товаров)
                      </option>
                    ))}
                  </select>
                  {isLoadingOneCItems && <p style={{ marginTop: '8px', color: '#666' }}>Загрузка...</p>}
                </div>
              </div>
            )}

            <div className={styles.excelUpload}>
              <h3 className={styles.subsectionTitle}>Загрузка Excel файла</h3>
              <div className={styles.fileUploadContainer}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    handleFileUpload(e);
                    setSelectedOneCItemId(null);
                  }}
                  className={styles.fileInput}
                  id="excel-file-input"
                />
                <label htmlFor="excel-file-input" className={styles.fileInputLabel}>
                  📄 Выберите Excel файл
                </label>
                <p className={styles.fileUploadHint}>Загрузите Excel файл с данными заказа</p>
              </div>
            </div>

          {editableItems.length > 0 && (
            <div className={styles.excelData}>
              <h3>Данные заказа</h3>
              <div className={styles.summary}>
                <p><strong>Поставщик:</strong> {excelData?.supplier || 'ИП Табанакова Владлена Николаевна'}</p>
                <p><strong>Покупатель:</strong> {excelData?.buyer || 'Покупатель'}</p>
                <p><strong>Сумма по файлу (руб):</strong> {originalTotal.toFixed(2)}</p>
                {deliveryAmountValue > 0 && (
                  <p><strong>Доставка (руб):</strong> {deliveryAmountValue.toFixed(2)}</p>
                )}
                {prepaymentAmountValue > 0 && (
                  <p><strong>Предоплата (руб):</strong> {prepaymentAmountValue.toFixed(2)}</p>
                )}
                <p><strong>Итого к доплате (руб):</strong> {finalAmount.toFixed(2)}</p>
                <div className={styles.customerInfoSection}>
                  <h3 className={styles.subsectionTitle}>Данные клиента</h3>
                  <div style={{ marginBottom: '16px' }}>
                    <label className={styles.customerLabel}>Выбрать клиента</label>
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder="Поиск по ФИО или E-mail"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px', marginBottom: 8 }}
                    />
                    <select
                      size={5}
                      value={selectedCustomerId || ''}
                      onChange={(e) => handleSelectCustomer(e.target.value ? parseInt(e.target.value) : null, false)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px', minHeight: '140px', boxSizing: 'border-box' }}
                    >
                      <option value="">-- Выберите клиента или введите данные вручную --</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.full_name} {customer.phone ? `(${customer.phone})` : ''} {customer.email ? `[${customer.email}]` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.customerFields}>
                    <div className={styles.customerField}>
                      <label className={styles.customerLabel}>ФИО *</label>
                      <input
                        type="text"
                        value={customerFullName}
                        onChange={(e) => {
                          setCustomerFullName(e.target.value);
                          setSelectedCustomerId(null);
                        }}
                        className={styles.customerInput}
                        placeholder="Иванов Иван Иванович"
                        required
                      />
                    </div>
                    <div className={styles.customerField}>
                      <label className={styles.customerLabel}>
                        E-mail <span className={styles.required}>*</span>
                      </label>
                      <input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => {
                          setCustomerEmail(e.target.value);
                          setSelectedCustomerId(null);
                        }}
                        className={styles.customerInput}
                        placeholder="name@example.com"
                        required
                      />
                    </div>
                    <div className={styles.customerField}>
                      <label className={styles.customerLabel}>
                        Телефон <span className={styles.required}>*</span>
                      </label>
                      <input
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => {
                          handlePhoneChange(e, setCustomerPhone);
                          setSelectedCustomerId(null);
                        }}
                        className={styles.customerInput}
                        placeholder="+7 / +375 / +994"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 20, padding: 16, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
                  <h4 style={{ marginBottom: 12, color: '#495057' }}>Связать с предоплатой (необязательно)</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>Номер предоплаты</label>
                      <input
                        type="text"
                        value={linkedPrepaymentNumber}
                        onChange={(e) => setLinkedPrepaymentNumber(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                        placeholder="УТ-12345"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>Сумма предоплаты (руб)</label>
                      <input
                        type="number" step="0.01"
                        value={linkedPrepaymentAmount}
                        onChange={(e) => setLinkedPrepaymentAmount(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>Доставка (руб)</label>
                      <input
                        type="number" step="0.01"
                        value={paymentDelivery}
                        onChange={(e) => setPaymentDelivery(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
                  <input type="checkbox" checked={customerConsent} onChange={(e) => setCustomerConsent(e.target.checked)} />
                  <span>
                  Согласен(а) с <Link to="/policy">политикой обработки персональных данных</Link>
                </span>
                </label>
                <div className={styles.paymentSummary}>
                  <p><strong>Общая сумма заказа (руб):</strong> {originalTotal.toFixed(2)}</p>
                  {prepaymentAmountValue > 0 && (
                    <p><strong>Предоплата (руб):</strong> {prepaymentAmountValue.toFixed(2)}</p>
                  )}
                  <p className={styles.finalAmount}><strong>Итого к доплате (руб):</strong> {finalAmount.toFixed(2)}</p>
                  <p><strong>Количество товаров:</strong> {editableItems.length}</p>
                </div>
              </div>

              <div className={styles.itemsList}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4>Товары:</h4>
                  <button
                    onClick={handleAddItem}
                    style={{
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    + Добавить товар
                  </button>
                </div>

                {editableItems.map((item, index) => (
                  <div key={index} className={styles.editableItem} style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                    background: '#f9f9f9'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h5>Товар {index + 1}</h5>
                      <button
                        onClick={() => handleRemoveItem(index)}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Удалить
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Название:</label>
                        <input
                          type="text"
                          value={item['Товар']}
                          onChange={(e) => handleItemChange(index, 'Товар', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Серия:</label>
                        <input
                          type="text"
                          value={item['Серия']}
                          onChange={(e) => handleItemChange(index, 'Серия', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Ширина:</label>
                        <input
                          type="number"
                          value={item['Шир']}
                          onChange={(e) => handleItemChange(index, 'Шир', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Состав:</label>
                        <input
                          type="text"
                          value={item['Состав']}
                          onChange={(e) => handleItemChange(index, 'Состав', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Производство:</label>
                        <input
                          type="text"
                          value={item['Пр-во']}
                          onChange={(e) => handleItemChange(index, 'Пр-во', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Количество:</label>
                        <input
                          type="number"
                          value={item['Количество']}
                          onChange={(e) => handleItemChange(index, 'Количество', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Цена (руб):</label>
                        <input
                          type="number"
                          value={item['Цена, руб']}
                          onChange={(e) => handleItemChange(index, 'Цена, руб', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1, marginRight: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Сумма (руб):</label>
                        <input
                          type="number"
                          value={item['Сумма, руб']}
                          onChange={(e) => handleItemChange(index, 'Сумма, руб', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div style={{
                        padding: '8px 12px',
                        background: '#e9ecef',
                        borderRadius: '4px',
                        fontSize: '14px',
                        color: '#495057'
                      }}>
                        Авто: {(item['Количество'] * item['Цена, руб']).toFixed(2)} руб
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button
                  onClick={handleCreatePayment}
                  className={styles.button}
                  disabled={isLoading}
                >
                  {isLoading ? 'Создание оплаты...' : 'Создать оплату'}
                </button>
              </div>
            </div>
          )}
          </div>

          {error && (
            <div style={{
              color: "red",
              marginTop: 16,
              padding: "12px 16px",
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: "4px",
              fontWeight: "500"
            }}>
              ❌ {error}
            </div>
          )}

          {successMessage && (
            <div style={{
              color: "#155724",
              marginTop: 16,
              padding: "12px 16px",
              backgroundColor: "#d4edda",
              border: "1px solid #c3e6cb",
              borderRadius: "4px",
              fontWeight: "500"
            }}>
              ✅ {successMessage}
            </div>
          )}

          {paymentUrl && (
            <div className={styles.qr}>
              <div className={styles.link}>Ссылка на оплату: <a href={paymentUrl} target="_blank" rel="noopener noreferrer">{paymentUrl}</a></div>
              <div style={{ marginTop: 16 }}>
                <QRCodeSVG value={paymentUrl} size={200} />
              </div>
            </div>
          )}

          <div className={styles.customerManagementSection} style={{ marginBottom: '32px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e9ecef' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 className={styles.sectionTitle} style={{ margin: 0 }}>Управление клиентами</h2>
              <button
                onClick={() => {
                  setShowCustomerForm(!showCustomerForm);
                  if (showCustomerForm) {
                    setEditingCustomer(null);
                    setNewCustomerFullName('');
                    setNewCustomerPhone('');
                    setNewCustomerEmail('');
                  }
                }}
                style={{
                  padding: '8px 16px',
                  background: '#1a7f37',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {showCustomerForm ? '✕ Отмена' : '+ Добавить клиента'}
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="🔍 Поиск по имени, email или телефону..."
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {showCustomerForm && (
              <div style={{ padding: '16px', background: 'white', borderRadius: '8px', marginBottom: '16px', border: '1px solid #dee2e6' }}>
                <h3 style={{ marginTop: 0, marginBottom: '16px' }}>{editingCustomer ? 'Редактировать клиента' : 'Новый клиент'}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>
                      ФИО <span style={{color: 'red'}}>*</span>
                    </label>
                    <input
                      type="text"
                      value={newCustomerFullName}
                      onChange={(e) => setNewCustomerFullName(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                      placeholder="Иванов Иван Иванович"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>Телефон</label>
                    <input
                      type="tel"
                      value={newCustomerPhone}
                      onChange={(e) => handlePhoneChange(e, setNewCustomerPhone)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                      placeholder="+7 / +375 / +994"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>Email</label>
                    <input
                      type="email"
                      value={newCustomerEmail}
                      onChange={(e) => setNewCustomerEmail(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                      placeholder="name@example.com"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleSaveCustomer}
                    disabled={isSavingCustomer}
                    style={{
                      padding: '8px 16px',
                      background: '#1a7f37',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isSavingCustomer ? 'not-allowed' : 'pointer',
                      fontWeight: '500',
                      opacity: isSavingCustomer ? 0.6 : 1
                    }}
                  >
                    {isSavingCustomer ? 'Сохранение...' : (editingCustomer ? 'Сохранить изменения' : 'Создать клиента')}
                  </button>
                  {editingCustomer && (
                    <button
                      onClick={() => {
                        setEditingCustomer(null);
                        setNewCustomerFullName('');
                        setNewCustomerPhone('');
                        setNewCustomerEmail('');
                        setShowCustomerForm(false);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Отмена
                    </button>
                  )}
                </div>
              </div>
            )}

            {customers.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <button
                  type="button"
                  onClick={() => setCustomersListCollapsed(!customersListCollapsed)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    marginBottom: customersListCollapsed ? 0 : '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    background: '#e9ecef',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <span>Сохраненные клиенты ({customers.length})</span>
                  <span style={{ fontSize: '14px', color: '#6c757d' }}>
                    {customersListCollapsed ? '▼ Развернуть' : '▲ Свернуть'}
                  </span>
                </button>
                {!customersListCollapsed && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '12px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    paddingRight: '4px'
                  }}>
                    {customers.map((customer) => (
                      <div
                        key={customer.id}
                        style={{
                          padding: '12px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #dee2e6',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{customer.full_name}</div>
                          {customer.phone && <div style={{ fontSize: '14px', color: '#6c757d' }}>📞 {customer.phone}</div>}
                          {customer.email && <div style={{ fontSize: '14px', color: '#6c757d' }}>✉️ {customer.email}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => handleEditCustomer(customer)}
                            style={{
                              padding: '4px 8px',
                              background: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteCustomer(customer.id)}
                            style={{
                              padding: '4px 8px',
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {customers.length === 0 && !isLoadingCustomers && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                Нет сохраненных клиентов. Добавьте первого клиента.
              </div>
            )}
            {isLoadingCustomers && (
              <div style={{ textAlign: 'center', padding: '20px' }}>Загрузка клиентов...</div>
            )}
          </div>

          {isAdmin && (
            <div className={styles.managerManagementSection} style={{ marginBottom: '32px', padding: '20px', background: '#fff3cd', borderRadius: '12px', border: '1px solid #ffc107' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 className={styles.sectionTitle} style={{ margin: 0, color: '#856404' }}>Управление менеджерами</h2>
                <button
                  onClick={() => {
                    setShowManagerForm(!showManagerForm);
                    if (showManagerForm) {
                      setEditingManager(null);
                      setManagerEmail('');
                      setManagerFullName('');
                      setManagerPhone('');
                      setManagerPassword('');
                      setManagerIsActive(true);
                      setManagerIsVerified(true);
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#ffc107',
                    color: '#000',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {showManagerForm ? '✕ Отмена' : '+ Добавить менеджера'}
                </button>
              </div>

              {showManagerForm && (
                <div style={{ padding: '16px', background: 'white', borderRadius: '8px', marginBottom: '16px', border: '1px solid #dee2e6' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '16px' }}>{editingManager ? 'Редактировать менеджера' : 'Новый менеджер'}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>
                        Email <span style={{color: 'red'}}>*</span>
                      </label>
                      <input
                        type="email"
                        value={managerEmail}
                        onChange={(e) => setManagerEmail(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                        placeholder="manager@example.com"
                        disabled={!!editingManager}
                      />
                    </div>
                    {!editingManager && (
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>
                          Пароль <span style={{color: 'red'}}>*</span>
                        </label>
                        <input
                          type="password"
                          value={managerPassword}
                          onChange={(e) => setManagerPassword(e.target.value)}
                          style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                          placeholder="Минимум 8 символов"
                        />
                      </div>
                    )}
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>ФИО</label>
                      <input
                        type="text"
                        value={managerFullName}
                        onChange={(e) => setManagerFullName(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                        placeholder="Иванов Иван Иванович"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: '14px' }}>Телефон</label>
                      <input
                        type="tel"
                        value={managerPhone}
                        onChange={(e) => handlePhoneChange(e, setManagerPhone)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: '14px' }}
                        placeholder="+7 / +375 / +994"
                      />
                    </div>
                    {editingManager && (
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '24px' }}>
                          <input
                            type="checkbox"
                            checked={managerIsActive}
                            onChange={(e) => setManagerIsActive(e.target.checked)}
                          />
                          <span>Активен</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '8px' }}>
                          <input
                            type="checkbox"
                            checked={managerIsVerified}
                            onChange={(e) => setManagerIsVerified(e.target.checked)}
                          />
                          <span>Подтвержден</span>
                        </label>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleSaveManager}
                      disabled={isSavingManager}
                      style={{
                        padding: '8px 16px',
                        background: '#ffc107',
                        color: '#000',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSavingManager ? 'not-allowed' : 'pointer',
                        fontWeight: '500',
                        opacity: isSavingManager ? 0.6 : 1
                      }}
                    >
                      {isSavingManager ? 'Сохранение...' : (editingManager ? 'Сохранить изменения' : 'Создать менеджера')}
                    </button>
                    <button
                      onClick={() => {
                        setEditingManager(null);
                        setManagerEmail('');
                        setManagerFullName('');
                        setManagerPhone('');
                        setManagerPassword('');
                        setManagerIsActive(true);
                        setManagerIsVerified(true);
                        setShowManagerForm(false);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              )}

              {managers.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>Менеджеры ({managers.length})</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                    {managers.map((manager) => (
                      <div
                        key={manager.id}
                        style={{
                          padding: '12px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #dee2e6',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{manager.full_name || manager.email}</div>
                          <div style={{ fontSize: '14px', color: '#6c757d' }}>✉️ {manager.email}</div>
                          {manager.phone && <div style={{ fontSize: '14px', color: '#6c757d' }}>📞 {manager.phone}</div>}
                          <div style={{ fontSize: '12px', marginTop: '4px' }}>
                            {manager.is_active ? '✅ Активен' : '❌ Неактивен'} | {manager.is_verified ? '✅ Подтвержден' : '❌ Не подтвержден'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => handleEditManager(manager)}
                            style={{
                              padding: '4px 8px',
                              background: '#ffc107',
                              color: '#000',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteManager(manager.id)}
                            style={{
                              padding: '4px 8px',
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {managers.length === 0 && !isLoadingManagers && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                  Нет менеджеров.
                </div>
              )}
              {isLoadingManagers && (
                <div style={{ textAlign: 'center', padding: '20px' }}>Загрузка менеджеров...</div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PaymentPage;
